import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import en from "./messages/en.json";
import sv from "./messages/sv.json";
import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  isAppLocale,
  readStoredLocale,
  writeStoredLocale,
  type AppLocale,
} from "./locales";
import { translate, type MessageTree, type Vars } from "./translate";

const catalogs: Record<AppLocale, MessageTree> = {
  sv: sv as MessageTree,
  en: en as MessageTree,
};

export type TFunction = (key: string, vars?: Vars) => string;

type I18nContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  t: TFunction;
  /** Messages for the active locale (rarely needed) */
  messages: MessageTree;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function resolveInitialLocale(): AppLocale {
  const stored = readStoredLocale();
  if (stored) return stored;
  if (typeof navigator !== "undefined") {
    const lang = (navigator.language || "").slice(0, 2).toLowerCase();
    if (isAppLocale(lang)) return lang;
  }
  return DEFAULT_LOCALE;
}

export type I18nProviderProps = {
  children: ReactNode;
  /** Override default (tests / SSR). Default: localStorage → browser → sv */
  initialLocale?: AppLocale;
};

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<AppLocale>(
    () => initialLocale ?? resolveInitialLocale(),
  );

  const setLocale = useCallback((next: AppLocale) => {
    if (!isAppLocale(next)) return;
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale === "sv" ? "sv" : "en";
    }
  }, [locale]);

  const messages = catalogs[locale] ?? catalogs[DEFAULT_LOCALE];
  const fallback = catalogs[FALLBACK_LOCALE];

  const t = useCallback<TFunction>(
    (key, vars) => translate(messages, fallback, key, vars),
    [messages, fallback],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, messages }),
    [locale, setLocale, t, messages],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within <I18nProvider>");
  }
  return ctx;
}

/** Convenience: only t() */
export function useT(): TFunction {
  return useI18n().t;
}
